import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Lightning, ArrowRight, Storefront, ChartLineUp, Robot, Wallet, Target, Megaphone, InstagramLogo, TiktokLogo, LinkedinLogo, YoutubeLogo, FacebookLogo, CurrencyBtc, Check } from "@phosphor-icons/react";
import { getToken } from "@/lib/api";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};
const money = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Nav = () => {
  const authed = !!getToken();
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0D14]/90 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <BrandLogo height={56} />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-mono-k tracking-widest-k text-[#A1A1AA]">
          <a href="#features" className="hover:text-[#FACC15]" data-testid="nav-features">FEATURES</a>
          <a href="#integrations" className="hover:text-[#FACC15]" data-testid="nav-integrations">INTEGRATIONS</a>
          <a href="#pricing" className="hover:text-[#FACC15]" data-testid="nav-pricing">PRICING</a>
        </nav>
        <div className="flex items-center gap-3">
          {authed ? (
            <Link to="/dashboard" className="k-btn primary" data-testid="nav-dashboard">Dashboard <ArrowRight size={16} /></Link>
          ) : (
            <>
              <Link to="/login" className="text-sm font-mono-k tracking-widest-k text-[#A1A1AA] hover:text-white" data-testid="nav-login">LOGIN</Link>
              <Link to="/register" className="k-btn primary" data-testid="nav-start">START FREE <ArrowRight size={16} /></Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const featureItems = [
  { icon: Storefront, title: "Bio + Shop", desc: "One page, endless links, real products. Sell digital or physical goods with checkout built in." },
  { icon: ChartLineUp, title: "Analytics & Funnels", desc: "Track every click, lead, and sale. Build funnels that convert cold traffic into loyal buyers." },
  { icon: Robot, title: "AI Copilot", desc: "Claude Sonnet 4.6 writes your funnels, generates keywords, and drafts campaign ideas." },
  { icon: Megaphone, title: "Paid Campaigns", desc: "Launch, track, and optimise ad campaigns across TikTok, Meta, LinkedIn, YouTube — from one canvas." },
  { icon: Wallet, title: "Wallet + Crypto", desc: "Accept PayPal or crypto. Withdraw earnings anytime. Your money, your rules." },
  { icon: Target, title: "Lead Capture", desc: "Native forms with UTM tracking. Every lead lands in your CRM inbox — no Zapier required." },
];

const socials = [
  { i: InstagramLogo, n: "INSTAGRAM" },
  { i: TiktokLogo, n: "TIKTOK" },
  { i: YoutubeLogo, n: "YOUTUBE" },
  { i: LinkedinLogo, n: "LINKEDIN" },
  { i: FacebookLogo, n: "FACEBOOK" },
  { i: CurrencyBtc, n: "CRYPTO" },
];

const Landing = () => {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/stats/public`).then(r => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0D14] text-white noise">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 k-grid opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 border border-white/20 px-3 py-1 mb-8">
                <span className="w-2 h-2 bg-[#FACC15]" />
                <span className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">V1 · BUILT FOR OPERATORS</span>
              </div>
              <h1 className="font-display font-black text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
                ONE LINK. <br />
                <span className="text-[#FACC15]">EVERY REVENUE</span> <br />
                STREAM.
              </h1>
              <p className="mt-8 max-w-xl text-lg text-[#A1A1AA] font-mono-k leading-relaxed">
                Korporate is a Linktree alternative for operators who want more than a bio page. Capture leads, run paid campaigns, sell products, accept crypto, and let AI write the copy that converts.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 items-center">
                <button onClick={() => nav("/register")} className="k-btn primary text-base" data-testid="hero-cta-start">
                  BUILD YOUR PAGE <ArrowRight size={18} />
                </button>
                <a href="#features" className="k-btn text-base" data-testid="hero-cta-features">
                  SEE THE STACK
                </a>
                {process.env.REACT_APP_PLAY_STORE_URL && (
                  <a
                    href={process.env.REACT_APP_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block hover:opacity-90 transition-opacity"
                    data-testid="hero-cta-playstore"
                    aria-label="Get Korporate on Google Play"
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      className="h-14"
                    />
                  </a>
                )}
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="k-card brutal-shadow">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">LIVE STATS</span>
                  <span className="w-2 h-2 bg-[#FACC15] animate-pulse" />
                </div>
                <div className="space-y-4">
                  {[
                    { l: "OPERATORS", v: stats ? fmt(stats.operators) : "…" },
                    { l: "TOTAL CLICKS", v: stats ? fmt(stats.clicks) : "…" },
                    { l: "LEADS CAPTURED", v: stats ? fmt(stats.leads) : "…" },
                    { l: "REVENUE PROCESSED", v: stats ? money(stats.revenue) : "…" },
                  ].map((r) => (
                    <div key={r.l} className="flex items-end justify-between border-b border-white/10 pb-3">
                      <span className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">{r.l}</span>
                      <span className="font-display text-2xl" data-testid={`stat-${r.l.toLowerCase().replace(/[^a-z]/g, "-")}`}>{r.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE integrations */}
      <section id="integrations" className="border-y border-white/10 bg-[#111318]">
        <div className="marquee py-6">
          <div className="marquee-track">
            {[...socials, ...socials, ...socials].map((s, i) => (
              <span key={i} className="inline-flex items-center gap-3 px-8 font-mono-k tracking-widest-k text-[#A1A1AA]">
                <s.i size={22} /> {s.n}
                <span className="mx-4 text-[#FACC15]">/</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— THE STACK</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tighter max-w-xl">Everything a link-in-bio should have. Nothing it shouldn't.</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/10">
          {featureItems.map((f, i) => (
            <div key={i} className="border-r border-b border-white/10 p-8 group hover:bg-[#111318] transition-colors" data-testid={`feature-${i}`}>
              <f.icon size={40} weight="duotone" className="text-[#FACC15] mb-6" />
              <h3 className="font-display text-2xl mb-3">{f.title}</h3>
              <p className="text-[#A1A1AA] font-mono-k text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-t border-white/10 bg-[#0A0D14] py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— PRICING</div>
          <h2 className="font-display text-5xl md:text-6xl tracking-tighter mb-16">Free forever. Pro when you're ready.</h2>
          <div className="grid md:grid-cols-2 gap-0 border border-white/10">
            <div className="p-10 border-r border-white/10">
              <div className="flex items-baseline justify-between mb-6">
                <h3 className="font-display text-4xl">FREE</h3>
                <div className="font-mono-k text-[#A1A1AA]">$0/mo</div>
              </div>
              <ul className="space-y-3 mb-10 font-mono-k text-sm">
                {["Unlimited links","Public bio page","Basic analytics","1 shop product","Lead capture form"].map(f => (
                  <li key={f} className="flex items-center gap-3"><Check size={16} className="text-[#FACC15]" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => nav("/register")} className="k-btn w-full justify-center" data-testid="pricing-free-cta">START FREE</button>
            </div>
            <div className="p-10 bg-[#111318] relative">
              <div className="absolute top-0 right-0 bg-[#FACC15] text-black px-3 py-1 text-xs font-mono-k tracking-widest-k">RECOMMENDED</div>
              <div className="flex items-baseline justify-between mb-6">
                <h3 className="font-display text-4xl text-[#FACC15]">PRO</h3>
                <div className="font-mono-k text-white">$19/mo</div>
              </div>
              <ul className="space-y-3 mb-10 font-mono-k text-sm">
                {["Everything in Free","Unlimited shop products","AI Copilot (Claude 4.6)","Advanced funnels","Paid campaign manager","Wallet + crypto payouts","Priority support"].map(f => (
                  <li key={f} className="flex items-center gap-3"><Check size={16} className="text-[#FACC15]" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => nav("/register")} className="k-btn primary w-full justify-center" data-testid="pricing-pro-cta">GET PRO <ArrowRight size={16} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs font-mono-k tracking-widest-k text-[#A1A1AA] flex-wrap gap-4">
          <div>© 2026 KORPORATE. ALL RIGHTS RESERVED.</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">PRIVACY</Link>
            <Link to="/terms" className="hover:text-white" data-testid="footer-terms">TERMS</Link>
            <a href="mailto:support@korporatelinks.com" className="hover:text-white">CONTACT</a>
          </div>
          <div>ONE LINK. EVERY REVENUE STREAM.</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
